import { get, set, unset } from 'lodash-es';

import { emptyObject } from 'ui-components/default-props/empty';
import { BulkActions } from '../enums/bulk-actions';

const bulkActionToDataMap = {
  [BulkActions.shipFrom.name]: {
    affectedProperties: ['shippingConfiguration.shipFromId', 'externalFulfillmentConfiguration.sellerFulfillmentProviderId'],
    isEmpty: (model) => {
      const shipFromId = model?.shippingConfiguration?.shipFromId;
      const sellerFulfillmentProviderId = model?.externalFulfillmentConfiguration?.sellerFulfillmentProviderId;
      return !shipFromId && !sellerFulfillmentProviderId;
    },
  },
  [BulkActions.service.name]: {
    affectedProperties: [
      'shippingConfiguration.sellerLabelProviderId',
      'shippingConfiguration.serviceId',
      'shippingConfiguration.serviceCollectionGuid',
      'externalFulfillmentConfiguration.fulfillmentServiceId',
    ],
  },
  [BulkActions.packageBulkAction.name]: {
    cleanFunction: (model, remainingBulkActions) => {
      const updatedModel = { ...model };
      const currentPackage = get(model, 'shippingConfiguration.packages[0]', emptyObject);
      delete currentPackage.packageTypeId;
      delete currentPackage.carrierId;
      delete currentPackage.name;
      delete currentPackage.isCustom;
      delete currentPackage.isHidden;
      delete currentPackage.packageId;
      if (!remainingBulkActions.find((bulkAction) => bulkAction.name === BulkActions.size.name)) {
        delete currentPackage.dimensions;
      }
      if (!remainingBulkActions.find((bulkAction) => bulkAction.name === BulkActions.weight.name)) {
        delete currentPackage.weight;
      }
      if (!remainingBulkActions.find((bulkAction) => bulkAction.name === BulkActions.insurance.name)) {
        delete currentPackage.insuredValue;
      }
      set(updatedModel, 'shippingConfiguration.packages[0]', currentPackage);
      return updatedModel;
    },
    isEmpty: (model) => {
      const currentPackage = get(model, 'shippingConfiguration.packages[0]', emptyObject);
      if (currentPackage === emptyObject) {
        return true;
      }
      const { packageTypeId } = currentPackage;
      return !packageTypeId;
    },
  },
  [BulkActions.confirmation.name]: {
    affectedProperties: ['shippingConfiguration.options.confirmation'],
  },
  [BulkActions.weight.name]: {
    affectedProperties: ['shippingConfiguration.packages[0].weight'],
    modelAdjuster: (model) => {
      if (!model.shippingConfiguration || !model.shippingConfiguration.packages) {
        return model;
      }
      const updatedModel = { ...model };
      delete updatedModel.shippingConfiguration.packages[0].weight;
      if (!updatedModel.shippingConfiguration.packages[0].dimensions) {
        delete updatedModel.shippingConfiguration.packages;
      }
      return updatedModel;
    },
  },
  [BulkActions.size.name]: {
    affectedProperties: ['shippingConfiguration.packages[0].dimensions'],
    modelAdjuster: (model) => {
      if (!model.shippingConfiguration || !model.shippingConfiguration.packages) {
        return model;
      }
      const updatedModel = { ...model };
      delete updatedModel.shippingConfiguration.packages[0].dimensions;
      if (!updatedModel.shippingConfiguration.packages.weight) {
        delete updatedModel.shippingConfiguration.packages;
      }
      return updatedModel;
    },
  },
  [BulkActions.insurance.name]: {
    affectedProperties: ['insureAmountOption', 'shippingConfiguration.insuranceProvider', 'shippingConfiguration.packages[0].insuredValue'],
  },
};

class BulkUpdateServiceClass {
  cleanModelForBulkAction(model, bulkActionId, remainingBulkActions) {
    const updatedModel = { ...model };
    const { affectedProperties, cleanFunction, modelAdjuster } = bulkActionToDataMap[bulkActionId];
    if (modelAdjuster) {
      return modelAdjuster(updatedModel);
    }
    if (affectedProperties) {
      affectedProperties.forEach((property) => unset(updatedModel, property));
      return updatedModel;
    }
    return cleanFunction(model, remainingBulkActions);
  }

  modelIsEmptyForBulkAction(model, bulkAction) {
    const { affectedProperties, isEmpty } = bulkActionToDataMap[bulkAction.name];
    if (affectedProperties) {
      return affectedProperties.reduce((sum, property) => {
        const modelProp = get(model, property);
        // allow for setting value to 0
        return sum && (modelProp === undefined || modelProp === null);
      }, true);
    }
    return isEmpty(model);
  }
}

export const BulkUpdateService = new BulkUpdateServiceClass();
